import { useState } from 'react';
import { useClerk } from '@clerk/clerk-react';
import { api } from '@aeon-karaoke/cloud-convex';
import { Button, Modal } from '@/component';
import { convex } from '@/lib/convex';
import { toResult } from '@/lib/result-adapter';

interface DeleteAccountModalProps {
  open: boolean;
  /** Whether the row holds a Stripe subscription, which the delete cancels. */
  subscribed: boolean;
  onClose: () => void;
}

/** What the account takes with it, as the sheet lists it: the Clerk user and
 * the public profile go, friendships go, uploads stay but nobody can play
 * them, and a subscription is cancelled at once. */
function goes(subscribed: boolean) {
  return [
    ['Sign-in and profile', 'removed'],
    ['Friends', 'removed'],
    ['Songs you made', 'no longer playable'],
    ...(subscribed ? [['Subscription', 'cancelled today']] : []),
  ] as const;
}

/** The confirmation behind Settings, Profile, Delete account. Mounted only
 * while open, so each opening starts clean and a closed modal touches no
 * Clerk state. */
export function DeleteAccountModal({ open, subscribed, onClose }: DeleteAccountModalProps) {
  if (!open) return null;
  return <DeleteAccountSheet subscribed={subscribed} onClose={onClose} />;
}

/** What goes, one button, then sign-out. `users.deleteMe` does the work; a
 * refusal stays on the sheet with its reason and the account untouched. */
function DeleteAccountSheet({ subscribed, onClose }: Omit<DeleteAccountModalProps, 'open'>) {
  const { signOut } = useClerk();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setPending(true);
    setError(null);
    const result = await toResult(() => convex.action(api.users.deleteMe, {}));
    if (!result.ok) {
      setPending(false);
      setError(result.error.message);
      return;
    }
    // Clerk already ended the session server-side; if clearing it locally
    // fails, a reload lands on the signed-out routes all the same.
    try {
      await signOut();
    } catch {
      window.location.assign('/');
    }
  }

  return (
    <Modal open onClose={onClose} title="Delete your account" kicker="Delete" kickerTone="down" maxWidth={520}>
      <div className="mt-[26px]">
        {goes(subscribed).map(([what, outcome]) => (
          <div key={what} className="flex items-baseline justify-between gap-4 border-b border-stripeA py-[13px]">
            <span className="text-sm text-mute">{what}</span>
            <span className="font-mono text-sm text-down">{outcome}</span>
          </div>
        ))}
      </div>
      <Button className="mt-6 w-full py-[14px] text-[15px]" disabled={pending} onClick={() => void confirm()}>
        Delete my account
      </Button>
      {error && <p className="mt-3 text-[13px] text-down">{error}</p>}
      <p className="mt-4 text-xs leading-[1.6] text-faint">
        This cannot be undone. You are signed out at once; signing in again starts a new account. Uploaded audio is kept
        for a retention period.
      </p>
    </Modal>
  );
}
